import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { api, throwIfNotOk } from '../lib/api';
import { formatUsd, formatDate } from '../lib/format';
import { useAuth } from '../lib/authContext';
import { DIRECTIONAL_PRICE_ONLY_MESSAGE } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { TelemetryScoreCallout } from '@siliconx/shared/ui/TelemetryScoreCallout';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import { LotAttributesCard } from '../components/lot-detail/LotAttributesCard';
import {
  LotUnitPricingCard,
  type LotOpenListing,
  type LotSkuGroup,
} from '../components/lot-detail/LotUnitPricingCard';

/** Watts -> kW, one decimal — the highest-value buyer-facing number on a
 * cabinet lot (Epic 1, plans/lots-first-class.md). No shared formatter
 * exists yet for this unit (grepped both portals) — small enough to keep
 * local rather than add a new packages/shared/src/ui/format.ts export for
 * two call sites (here and routes/lots.tsx). */
function formatKw(watts: number | null): string {
  if (watts == null) return '—';
  return `${(watts / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} kW`;
}

// SIL-564: three tabs — Overview / Assets / Activity — URL-driven so a deep
// link survives reload. Unknown/missing `tab` falls back to 'overview'.
type LotTab = 'overview' | 'assets' | 'activity';
const TAB_SEARCH = (search: Record<string, unknown>): { tab: LotTab } => {
  const tab: LotTab = search.tab === 'assets' || search.tab === 'activity' ? search.tab : 'overview';
  return { tab };
};

export const Route = createFileRoute('/lots_/$lotId')({
  component: LotDetailPage,
  validateSearch: TAB_SEARCH,
});

interface ClassAgg { assetClassId: string; assetClassName: string; count: number; reporting: number; fmvMedUsd: number }
interface TeleClass {
  assetClassId: string;
  assetClassName: string;
  reporting: number;
  total: number;
  averages: Record<string, number | null>;
}
interface HealthScore {
  overall: number;
  coverage: number;
  scoredAt: string;
  categories: Array<{ category: string; score: number; coverage: number }>;
}
interface Member { id: string; serialNumber: string | null; assetClass: string; fmvMedUsd: number | null; healthScore: number | null; allowsListing: boolean; lifecycleState: string | null }
interface CabinetSpanEntry { assetClassId: string; assetClassName: string; count: number }
interface LotDetail {
  lot: {
    id: string; name: string; lotType: 'cluster' | 'lot' | 'rack'; status: string; notes: string | null;
    teamId: string | null; teamName: string | null;
    dataCenterId: string | null; dataCenterName: string | null;
    internalLocation: string | null; isCompleteCabinet: boolean | null;
  };
  memberCount: number;
  /** SIL-2063: an open (draft/active/under_offer/pending_settlement) lot listing freezes membership. */
  hasOpenListing: boolean;
  /** SIL-2062: per-SKU rows for the unit-price table (skuId null = member with no matched SKU). */
  skuGroups: LotSkuGroup[];
  /** SIL-2062: the open lot listing with its current total + per-SKU unit prices, or null. */
  listing: LotOpenListing | null;
  listable: boolean;
  aggregate: { fmvMedTotalUsd: number; includesDirectionalBaseline?: boolean; reporting: number; total: number; fmvPricedAt: string | null; conditionBands: Array<{ band: string; count: number }>; netRecoveryTotalUsd: number; healthScore: HealthScore | null; scoredCount: number; byClass: ClassAgg[] };
  totalPowerDrawWatts: number | null;
  powerDrawReporting: number;
  cabinetSpan: CabinetSpanEntry[];
  members: Member[];
  telemetryByClass: TeleClass[];
}
interface AssetOpt {
  id: string;
  serialNumber: string | null;
  assetClass: string;
  manufacturerName: string | null;
  modelName: string | null;
  fmvMedianUsd: number | null;
  teamId: string | null;
}
interface ActivityEvent {
  id: string;
  type: 'lot_created' | 'fmv_refresh' | 'telemetry_score';
  label: string;
  detail: string;
  occurredAt: string;
}

// "Dell PowerEdge R750" when make/model are known, else the class — the serial
// alone was not a usable identifier in the old dropdown.
function assetLabel(a: AssetOpt): string {
  return [a.manufacturerName, a.modelName].filter(Boolean).join(' ') || a.assetClass;
}

const TELE_LABELS: Record<string, string> = {
  avgGpuTempC: 'Avg GPU °C',
  avgCpuTempC: 'Avg CPU °C',
  avgPowerDrawWatts: 'Avg power (W)',
  avgGpuUtilizationPct: 'Avg GPU util %',
  totalUptimeSeconds: 'Uptime (s)',
  totalEccErrors: 'ECC errors',
  maxXidErrorCode: 'Max XID',
};

function Toast({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={cn('fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg', type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white')}>
      {message}
    </div>
  );
}

/**
 * Searchable asset picker — replaces the old serial-only <select>. Typeahead
 * filters over make/model/serial/class; each row leads with a human label
 * ("Dell PowerEdge R750") and shows serial · class · FMV underneath so the
 * analyst can actually tell two assets apart. Same combobox conventions as
 * MakeModelPicker (keyboard nav, mousedown-before-blur).
 */
function AssetPicker({
  assets, value, onChange, disabled,
}: {
  assets: AssetOpt[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const hiRef = useRef<HTMLButtonElement | null>(null);

  const selected = value ? assets.find((a) => a.id === value) ?? null : null;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? assets.filter((a) =>
        [a.manufacturerName, a.modelName, a.serialNumber, a.assetClass]
          .filter(Boolean)
          .some((f) => (f as string).toLowerCase().includes(q)),
      )
    : assets;
  const highlighted = Math.min(hi, Math.max(0, filtered.length - 1));

  useEffect(() => { hiRef.current?.scrollIntoView({ block: 'nearest' }); }, [highlighted, open]);

  function pick(a: AssetOpt) { onChange(a.id); setOpen(false); setQuery(''); }
  function clear() { onChange(''); setQuery(''); setOpen(true); setHi(0); }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(Math.min(highlighted + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(Math.max(highlighted - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const a = filtered[highlighted]; if (a) pick(a); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  const showList = open && !selected && filtered.length > 0;

  return (
    <div className="relative w-80">
      {selected ? (
        <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-brand-300 bg-brand-50 px-3 text-sm">
          <span className="truncate text-foreground">
            {assetLabel(selected)}
            <span className="ml-1.5 text-muted-foreground">· {selected.serialNumber ?? 'no serial'}</span>
          </span>
          <button
            type="button"
            aria-label="Clear selected asset"
            disabled={disabled}
            onClick={clear}
            className="flex-none rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <input
          value={query}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
          placeholder="Search assets by make, model, serial, or class…"
          className="h-9 w-full rounded-md border border-border px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      )}
      {showList && (
        <div className="absolute left-0 z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-md">
          {filtered.map((a, i) => (
            <button
              type="button"
              key={a.id}
              ref={i === highlighted ? hiRef : undefined}
              onMouseDown={(e) => { e.preventDefault(); pick(a); }}
              onMouseEnter={() => setHi(i)}
              className={cn(
                'flex w-full flex-col gap-0.5 px-3 py-2 text-left',
                i === highlighted ? 'bg-brand-50' : '',
              )}
            >
              <span className="text-sm font-medium text-foreground">{assetLabel(a)}</span>
              <span className="text-xs text-muted-foreground">
                {a.serialNumber ?? 'no serial'} · {a.assetClass}
                {a.fmvMedianUsd != null && ` · ${formatUsd(a.fmvMedianUsd)}`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LotDetailPage() {
  const { lotId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState<LotDetail | null>(null);
  const [assets, setAssets] = useState<AssetOpt[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addSel, setAddSel] = useState('');
  const [addClassFilter, setAddClassFilter] = useState('');
  const [busy, setBusy] = useState(false);
  // SIL-1848: seller-supplied price, required when the lot has a directional member.
  const [lotPriceStr, setLotPriceStr] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Poach-confirm dialog (SIL fix): set when the API rejects an add because
  // the asset already belongs to a DIFFERENT lot (409 already_in_lot).
  const [poachConfirm, setPoachConfirm] = useState<{ assetId: string; currentLotName: string } | null>(null);

  const notify = useCallback((m: string, t: 'success' | 'error') => setToast({ message: m, type: t }), []);

  const goTab = useCallback((t: LotTab) => {
    void navigate({ to: '/lots/$lotId', params: { lotId }, search: { tab: t } });
  }, [navigate, lotId]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.api.client.lots[':lotId'].$get({ param: { lotId } }).then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as LotDetail;
      }),
      api.api.client.assets.$get({ query: { limit: '200' } }).then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as { data: AssetOpt[] };
      }),
      api.api.client.lots[':lotId'].activity.$get({ param: { lotId } }).then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as { events: ActivityEvent[] };
      }),
    ])
      .then(([d, a, act]) => { setDetail(d); setAssets(a.data); setActivity(act.events); })
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [lotId]);

  useEffect(() => { load(); }, [load]);

  async function addAsset() {
    if (!addSel) return;
    await addAssetToLot(addSel, false);
  }

  // confirm: false on the first attempt for a given assetId; true on the
  // user's explicit confirm from the poach dialog below. The API rejects an
  // unconfirmed add with 409 { error: 'already_in_lot', currentLotId,
  // currentLotName } when the asset already belongs to a DIFFERENT lot —
  // show that as a confirmation dialog instead of a generic error toast, and
  // only reassign the asset once the user explicitly confirms the move.
  async function addAssetToLot(assetId: string, confirm: boolean) {
    setBusy(true);
    try {
      const res = await api.api.client.lots[':lotId'].members.$post({
        param: { lotId },
        json: { assetId, confirm },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
          currentLotId?: string;
          currentLotName?: string;
        };
        if (res.status === 409 && body.error === 'already_in_lot') {
          setPoachConfirm({ assetId, currentLotName: body.currentLotName ?? 'another lot' });
          return;
        }
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      setAddSel('');
      setPoachConfirm(null);
      load();
      notify('Asset added to lot', 'success');
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function removeAsset(assetId: string) {
    setBusy(true);
    try {
      const res = await api.api.client.lots[':lotId'].members[':assetId'].$delete({
        param: { lotId, assetId },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
      load();
      notify('Asset removed', 'success');
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function updateLotType(lotType: 'cluster' | 'lot' | 'rack') {
    setBusy(true);
    try {
      const res = await api.api.client.lots[':lotId'].$patch({ param: { lotId }, json: { lotType } });
      await throwIfNotOk(res);
      load();
      notify('Lot type updated', 'success');
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function listLot() {
    setBusy(true);
    try {
      const price = Number(lotPriceStr);
      const res = await api.api.client.listings.$post({
        json: {
          listingType: 'lot',
          lotId,
          ...(lotPriceStr.trim() !== '' && price > 0 ? { askingPriceUsd: price } : {}),
        },
      });
      await throwIfNotOk(res);
      notify('Lot listed', 'success');
      load();
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  // SIL-2062: error text from the API's `{ error: { code, message } }` / `{ error: 'x' }` shapes.
  async function failureMessage(res: Response): Promise<string> {
    const body = (await res.json().catch(() => ({}))) as { error?: string | { message?: string }; message?: string };
    const e = body.error;
    return (typeof e === 'object' ? e?.message : undefined) ?? body.message ?? (typeof e === 'string' ? e : `HTTP ${res.status}`);
  }

  async function listLotWithUnitPrices(prices: Array<{ skuId: string; unitPriceUsd: number }>) {
    setBusy(true);
    try {
      const res = await api.api.client.listings.$post({
        json: { listingType: 'lot', lotId, unitPrices: prices },
      });
      if (!res.ok) throw new Error(await failureMessage(res as unknown as Response));
      notify('Lot listed at unit prices', 'success');
      load();
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function saveUnitPrices(prices: Array<{ skuId: string; unitPriceUsd: number }>) {
    const listingId = detail?.listing?.id;
    if (!listingId) return;
    setBusy(true);
    try {
      const res = await api.api.client.listings[':id']['unit-prices'].$put({
        param: { id: listingId },
        json: { unitPrices: prices },
      });
      if (!res.ok) throw new Error(await failureMessage(res as unknown as Response));
      notify('Prices updated', 'success');
      load();
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  async function deleteLot() {
    if (!confirm('Delete this lot?')) return;
    try {
      const res = await api.api.client.lots[':lotId'].$delete({ param: { lotId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      void navigate({ to: '/lots' });
    } catch (ex: unknown) { notify((ex as Error).message, 'error'); }
  }

  if (loading) return <LoadingShell rows={5} />;
  if (error) return <ErrorState title="Couldn't load this lot." error={new Error(error)} onRetry={load} />;
  if (!detail) return null;

  const memberIds = new Set(detail.members.map((m) => m.id));
  // A lot only ever holds ONE team's assets (client_set_asset_lot's
  // data-integrity check, migration 735) — offering another team's asset in the
  // picker only produced a server rejection, so filter them out here.
  const addable = assets.filter((a) => !memberIds.has(a.id) && a.teamId === detail.lot.teamId);
  // Distinct classes among addable assets, for the "All classes" filter next to
  // the picker — client-side, since the /api/client/assets response already
  // carries assetClass on every row.
  const addableClasses = Array.from(new Set(addable.map((a) => a.assetClass))).sort();
  const addableFiltered = addClassFilter ? addable.filter((a) => a.assetClass === addClassFilter) : addable;

  return (
    <>
      <div className="space-y-6">
        <div>
          <BackLink as={Link} to="/lots">Lots</BackLink>
          <PageHeader
            className="mt-1"
            title={detail.lot.name}
            subtitle={`${detail.memberCount} asset${detail.memberCount === 1 ? '' : 's'} · ${detail.lot.lotType === 'cluster' ? 'Cluster' : detail.lot.lotType === 'rack' ? 'Rack' : 'Lot'}`}
            actions={
              <div className="flex items-center gap-2">
                <select
                  value={detail.lot.lotType}
                  disabled={busy}
                  onChange={(e) => updateLotType(e.target.value as 'cluster' | 'lot' | 'rack')}
                  className="cursor-pointer rounded-full border-0 bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-500"
                  aria-label="Lot type"
                >
                  <option value="lot">Lot</option>
                  <option value="cluster">Cluster</option>
                  <option value="rack">Rack</option>
                </select>
                {!detail.listable && detail.lot.status !== 'listed' && (
                  <span className="text-xs text-amber-600">
                    {detail.members.length === 0
                      ? 'Add assets to list this lot'
                      : `${detail.members.filter((m) => !m.allowsListing).length} of ${detail.members.length} assets not in a listable state`}
                  </span>
                )}
                {detail.aggregate.includesDirectionalBaseline === true && detail.lot.status !== 'listed' && (
                  <div className="flex items-center gap-2" data-testid="lot-directional-price">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lotPriceStr}
                      onChange={(e) => setLotPriceStr(e.target.value)}
                      placeholder="Asking price (USD)"
                      aria-label="Lot asking price"
                      className="w-40 rounded-md border border-border px-2 py-1 text-sm"
                    />
                    <span className="text-xs text-amber-700">{DIRECTIONAL_PRICE_ONLY_MESSAGE}</span>
                  </div>
                )}
                <Button size="sm" className="text-sm"
                  disabled={
                    busy ||
                    !detail.listable ||
                    detail.lot.status === 'listed' ||
                    (detail.aggregate.includesDirectionalBaseline === true && !(Number(lotPriceStr) > 0))
                  }
                  onClick={listLot}
                  title={!detail.listable ? 'Every member must be in a listable state' : ''}
                >
                  {detail.lot.status === 'listed' ? 'Listed' : 'List this lot'}
                </Button>
                <button type="button" onClick={deleteLot} className="rounded-md px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">Delete</button>
              </div>
            }
          />
        </div>

        {/* SIL-564: Overview / Assets / Activity tabs, URL-driven via `tab`
            search param so a deep link survives reload. */}
        <div className="flex gap-1 border-b border-border">
          {([
            ['overview', 'Overview'],
            ['assets', `Assets (${detail.memberCount})`],
            ['activity', 'Activity'],
          ] as Array<[LotTab, string]>).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => goTab(t)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm',
                tab === t
                  ? 'border-brand-600 font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

      {tab === 'overview' && (
      <div className="space-y-6">
        {/* Aggregate FMV + total power draw hero — equal-weight side by side.
            kW is the highest-value buyer-facing number on a cabinet lot
            (Epic 1, plans/lots-first-class.md) — it gets the same visual
            prominence as the FMV figure, not a small stat tile below it. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Aggregate FMV (median)
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <span className="text-3xl font-semibold text-foreground">{formatUsd(detail.aggregate.fmvMedTotalUsd)}</span>
              {detail.aggregate.fmvPricedAt && (
                <span className="text-xs text-muted-foreground">Priced {formatDate(detail.aggregate.fmvPricedAt)}</span>
              )}
              {detail.aggregate.reporting < detail.aggregate.total && (
                <span className="text-xs text-amber-600">{detail.aggregate.reporting} of {detail.aggregate.total} priced</span>
              )}
            </div>
            {(detail.aggregate.conditionBands?.length ?? 0) > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
                {detail.aggregate.conditionBands.map((c) => (
                  <span key={c.band} className="inline-flex items-center gap-1.5">
                    <ConditionBadge value={c.band} />
                    <span className="text-xs font-medium text-muted-foreground">×{c.count}</span>
                  </span>
                ))}
              </div>
            )}
          </Card>
          <Card>
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Total power draw
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <span className="text-3xl font-semibold text-foreground">{formatKw(detail.totalPowerDrawWatts)}</span>
              {detail.powerDrawReporting < detail.memberCount && (
                <span className="text-xs text-amber-600">{detail.powerDrawReporting} of {detail.memberCount} reporting</span>
              )}
            </div>
            {detail.cabinetSpan.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
                {detail.cabinetSpan.map((c) => (
                  <span
                    key={c.assetClassId}
                    className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    {c.assetClassName} ×{c.count}
                  </span>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* SIL-2062: per-SKU asking price per unit (create + edit of a listed lot). */}
        <LotUnitPricingCard
          skuGroups={detail.skuGroups ?? []}
          listing={detail.listing ?? null}
          listable={detail.listable}
          busy={busy}
          onList={listLotWithUnitPrices}
          onSave={saveUnitPrices}
        />

        {/* Location + managing team + cabinet attributes (Epic 1). */}
        <LotAttributesCard
          lotId={lotId}
          dataCenterId={detail.lot.dataCenterId}
          dataCenterName={detail.lot.dataCenterName}
          internalLocation={detail.lot.internalLocation}
          isCompleteCabinet={detail.lot.isCompleteCabinet}
          teamId={detail.lot.teamId}
          teamName={detail.lot.teamName}
          isEmpty={detail.memberCount === 0}
          canEditTeam={user?.role === 'admin'}
          onSaved={load}
          notify={notify}
        />

        {/* Net recovery + asset count */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card padding="md">
            <div className="text-xs text-muted-foreground">Net recovery</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">{formatUsd(detail.aggregate.netRecoveryTotalUsd)}</div>
          </Card>
          <Card padding="md">
            <div className="text-xs text-muted-foreground">Assets</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">{detail.memberCount}</div>
          </Card>
        </div>

        {/* Lot-average telemetry health — same ring/tier/category breakdown as the
            asset detail page, averaged across all scored members. */}
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">Avg telemetry health</div>
            {detail.aggregate.scoredCount < detail.aggregate.total && (
              <div className="text-xs text-amber-600">{detail.aggregate.scoredCount} of {detail.aggregate.total} scored</div>
            )}
          </div>
          {detail.aggregate.healthScore ? (
            <TelemetryScoreCallout
              label="Lot avg health score"
              overall={detail.aggregate.healthScore.overall}
              coverage={detail.aggregate.healthScore.coverage}
              scoredAt={detail.aggregate.healthScore.scoredAt}
              categories={detail.aggregate.healthScore.categories}
              metaLine={`Averaged across ${detail.aggregate.scoredCount} scored ${detail.aggregate.scoredCount === 1 ? 'asset' : 'assets'} · ring shows telemetry coverage`}
            />
          ) : (
            <div className="text-sm text-muted-foreground">No telemetry health scores yet for this lot.</div>
          )}
        </Card>

        {/* Per-class FMV subtotal */}
        {detail.aggregate.byClass.length > 0 && (
          <Card>
            <PanelTitle title="By asset class" />
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2">Class</th><th className="px-4 py-2">Count</th><th className="px-4 py-2">FMV subtotal</th><th className="px-4 py-2">Priced</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {detail.aggregate.byClass.map((c) => (
                    <tr key={c.assetClassId}>
                      <td className="px-4 py-2 text-foreground">{c.assetClassName}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.count}</td>
                      <td className="px-4 py-2 text-foreground">{formatUsd(c.fmvMedUsd)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.reporting} of {c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Per-class telemetry averages */}
        {detail.telemetryByClass.length > 0 && (
          <Card>
            <PanelTitle title="Telemetry by class" description="Latest daily average" />
            <div className="mt-3 space-y-3">
              {detail.telemetryByClass.map((tc) => (
                <div key={tc.assetClassId} className="rounded-lg border border-border bg-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{tc.assetClassName}</span>
                    <span className="text-xs text-muted-foreground">{tc.reporting} of {tc.total} reporting</span>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(TELE_LABELS).map(([k, label]) => (
                      <div key={k}>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                        <div className="text-sm text-foreground">
                          {tc.averages[k] == null ? '—' : Math.round((tc.averages[k] as number) * 10) / 10}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      )}

      {tab === 'assets' && (
      <div className="space-y-6">
        {/* Members + add */}
        <Card>
          <PanelTitle title="Assets in this lot" />
          {detail.hasOpenListing && (
            <div className="mt-3 text-xs text-amber-700" data-testid="lot-membership-frozen">
              This lot has an open listing, so its assets cannot be changed. Withdraw the listing to add or remove assets.
            </div>
          )}
          <div className="mt-3 flex items-start gap-2">
            <select
              value={addClassFilter}
              disabled={busy || detail.hasOpenListing}
              onChange={(e) => { setAddClassFilter(e.target.value); setAddSel(''); }}
              className="h-9 rounded-md border border-border px-2 text-sm text-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              aria-label="Filter addable assets by class"
            >
              <option value="">All classes</option>
              {addableClasses.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
            <AssetPicker assets={addableFiltered} value={addSel} onChange={setAddSel} disabled={busy || detail.hasOpenListing} />
            <Button className="h-9 px-3" disabled={busy || detail.hasOpenListing || !addSel} onClick={addAsset}>Add</Button>
          </div>
          {addable.length === 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              No assets on {detail.lot.teamName ?? 'this lot’s team'} are available to add. A lot can only hold one
              team&rsquo;s assets{detail.memberCount === 0 ? ' — set the lot’s team below to match the assets you want to add.' : '.'}
            </div>
          )}
          <div className="mt-3 overflow-hidden rounded-lg border border-border">
            {detail.members.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No assets in this lot yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2">Serial</th><th className="px-4 py-2">Class</th><th className="px-4 py-2">FMV</th><th className="px-4 py-2">Health</th><th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {detail.members.map((m) => (
                    <tr
                      key={m.id}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => void navigate({ to: '/assets/$assetId', params: { assetId: m.id } })}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-foreground">
                        <span className="inline-flex items-center gap-2">
                          {m.serialNumber ?? '—'}
                          {!m.allowsListing && (
                            <span
                              className="rounded-full bg-amber-100 px-2 py-0.5 font-sans text-xs font-medium text-amber-700"
                              title="This asset's lifecycle state blocks listing the lot"
                            >
                              Not listable{m.lifecycleState ? ` · ${m.lifecycleState}` : ''}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-foreground">{m.assetClass}</td>
                      <td className="px-4 py-2 text-foreground">{m.fmvMedUsd == null ? '—' : formatUsd(m.fmvMedUsd)}</td>
                      <td className="px-4 py-2 text-foreground">{m.healthScore == null ? '—' : Math.round(m.healthScore)}</td>
                      <td className="px-4 py-2 text-right">
                        <button type="button" disabled={busy || detail.hasOpenListing} title={detail.hasOpenListing ? 'Withdraw the listing to change the lot' : undefined} onClick={(e) => { e.stopPropagation(); removeAsset(m.id); }} className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
      )}

      {tab === 'activity' && (
      <div className="space-y-6">
        {/* SIL-564: derived from real append-only history (fmv_pricing_snapshot,
            telemetry_score_snapshot) + lot creation — NOT a fabricated
            membership/lifecycle event log (no such audit trail exists yet,
            see apps/api/src/routes/client/lots.ts activity endpoint comment). */}
        <Card>
          <PanelTitle title="Lot activity" description="FMV and telemetry scoring history for this lot's current members." />
          {!activity || activity.length === 0 ? (
            <div className="mt-3 text-sm text-muted-foreground">No activity recorded yet.</div>
          ) : (
            <ol className="mt-3 space-y-3">
              {activity.map((ev) => (
                <li key={ev.id} className="border-l-2 border-border pl-3">
                  <div className="text-sm font-medium text-foreground">{ev.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(ev.occurredAt)}
                    {ev.detail && ` · ${ev.detail}`}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
      )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {poachConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
            <h2 className="mb-2 text-base font-semibold text-foreground">Move asset to this lot?</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              This asset already belongs to <span className="font-medium text-foreground">{poachConfirm.currentLotName}</span>.
              Adding it here will remove it from that lot.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPoachConfirm(null)}
                disabled={busy}
                className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <Button
                disabled={busy}
                onClick={() => { void addAssetToLot(poachConfirm.assetId, true); }}
              >
                {busy ? 'Moving…' : 'Move it here'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
